import React, { ReactNode, CSSProperties, forwardRef } from 'react';
import { Input, InputProps, InputRef } from 'antd';
import { EyeFilled, EyeInvisibleFilled } from '@ant-design/icons';
import { legacyTextColor } from '@/utils/legacyColor';

interface CustomInputProps extends Omit<InputProps, 'style'> {
  width?: string;
  height?: string;
  border?: string;
  bgColor?: string;
  textColor?: string;
  borderRadius?: string;
  placeholderColor?: string;
  lineHeight?: string;
  type?: 'text' | 'password';
  suffix?: ReactNode;
  suffixFontWeight?: string;
  suffixColor?: string;
  prefix?: ReactNode;
  boxShadow?: string;
  disabled?: boolean;
  children?: ReactNode;
  value?: string;
  style?: React.CSSProperties;
}

const CustomInput = forwardRef<InputRef, CustomInputProps>(
  (
    {
      width,
      height,
      border,
      bgColor,
      textColor,
      borderRadius,
      placeholderColor,
      lineHeight,
      type = 'text',
      suffix,
      suffixFontWeight = '400',
      suffixColor = 'var(--text-secondary)',
      prefix,
      boxShadow,
      disabled,
      children,
      value,
      className,
      ...props
    },
    ref
  ) => {
    const { status, style: propsStyle, ...inputProps } = props;
    const resolvedBorder =
      status === 'error'
        ? '1px solid var(--danger)'
        : status === 'warning'
          ? '1px solid var(--warning)'
          : border || 'none';

    const resolvedPlaceholder = placeholderColor || 'var(--text-secondary)';

    const customStyle: CSSProperties = {
      height: height || '36px',
      width: width || '100%',
      border: resolvedBorder,
      backgroundColor: bgColor || 'var(--light-gray)',
      color: textColor || 'var(--text-primary)',
      borderRadius: borderRadius || '8px',
      lineHeight: lineHeight || 'normal',
      boxShadow: boxShadow || 'none',
      // Real CSS (see .zl-custom-input in global.scss) reads this for ::placeholder.
      ['--zl-input-placeholder' as string]: resolvedPlaceholder,
      ...(propsStyle && typeof propsStyle === 'object' ? propsStyle : {}),
    };

    const suffixStyle: CSSProperties = {
      fontWeight: suffixFontWeight,
      color: legacyTextColor(suffixColor),
    };

    const mergedClassName = ['zl-custom-input', className]
      .filter(Boolean)
      .join(' ');

    if (children) {
      // eslint-disable-next-line react/forbid-dom-props -- the style is built from the width, height and colour props
      return <div style={{ ...customStyle, border: '0' }}>{children}</div>;
    }

    if (type === 'password') {
      return (
        <Input.Password
          ref={ref}
          className={mergedClassName}
          style={customStyle}
          suffix={suffix}
          prefix={prefix}
          disabled={disabled}
          value={value}
          status={status}
          iconRender={(visible) =>
            visible ? (
              <EyeFilled style={{ color: 'var(--text-primary)' }} />
            ) : (
              <EyeInvisibleFilled style={{ color: 'var(--text-primary)' }} />
            )
          }
          {...inputProps}
        />
      );
    }

    return (
      <Input
        ref={ref}
        className={mergedClassName}
        style={customStyle}
        // eslint-disable-next-line react/forbid-dom-props -- the suffix weight and colour come from props
        suffix={<span style={{ ...suffixStyle }}>{suffix}</span>}
        prefix={prefix}
        disabled={disabled}
        value={value}
        status={status}
        {...inputProps}
      />
    );
  }
);

CustomInput.displayName = 'CustomInput';

export default CustomInput;
